import { useEffect, useMemo, useState } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import {
  DASHBOARD_ROLE_CLAIM,
  userClaimsToMap,
  visibleFeatureIds,
} from "../../auth/featureScopes";
import RoleModal from "../../components/roles/RoleModal";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useCustomerRoles from "../../data/useCustomerRoles";
import useWilmaUsers from "../../data/useWilmaUsers";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { ICustomerRole } from "../../models/customerRole/ICustomerRole";
import { ContactType } from "../../models/Contact";

const contactTypeLabel = (type: number) => {
  switch (type) {
    case ContactType.Admin:
      return "Admin";
    case ContactType.Staff:
      return "Staff";
    case ContactType.Driver:
      return "Driver";
    case ContactType.Mechanic:
      return "Mechanic";
    case ContactType.Exporter:
      return "Exporter";
    default:
      return "Unknown";
  }
};

export default function Roles() {
  const { setInfo } = usePageNameContext();
  const { roles } = useCustomerRoles();
  const { wilmaUsers } = useWilmaUsers();
  const isTme = CheckFeatureFlag("TME");
  const featureIds = visibleFeatureIds(isTme);
  const [filtered, setFiltered] = useState<ICustomerRole[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editRole, setEditRole] = useState<ICustomerRole | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);

  const userCountByRole = useMemo(() => {
    const counts = new Map<string, number>();
    const users = wilmaUsers.data?.data ?? [];
    for (const user of users) {
      const roleId = userClaimsToMap(user.userClaims)[DASHBOARD_ROLE_CLAIM];
      if (!roleId || roleId === "custom") continue;
      const key = roleId.toLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [wilmaUsers.data]);

  useEffect(() => {
    setInfo({
      name: "Roles",
      desc: "Create roles, control feature access, and assign staff",
    });
  }, []);

  useEffect(() => {
    const all = roles.data?.data ?? [];
    const st = searchTerm.toLowerCase();
    setFiltered(
      all.filter(
        (role) =>
          role.name?.toLowerCase().includes(st) ||
          role.description?.toLowerCase().includes(st)
      )
    );
  }, [roles.data, searchTerm]);

  const handleAdd = () => {
    setEditRole(undefined);
    setModalOpen(true);
  };

  const handleEdit = (role: ICustomerRole) => {
    setEditRole(role);
    setModalOpen(true);
  };

  if (roles.isLoading) {
    return (
      <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <>
      <RoleModal open={modalOpen} setOpen={setModalOpen} role={editRole} />
      <div className="mt-4 flex justify-between gap-4">
        <div className="relative flex items-stretch flex-grow focus-within:z-10">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
          <input
            type="text"
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 sm:text-sm"
            placeholder="Role name"
          />
        </div>
        <button
          className="bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
          type="button"
          onClick={handleAdd}
        >
          Add Role
        </button>
      </div>

      <div className="mt-4 overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                Name
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Base type
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Users
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Features with access
              </th>
              <th className="relative py-3.5 pl-3 pr-4">
                <span className="sr-only">Edit</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filtered.map((role) => {
              const enabledCount = featureIds.filter(
                (id) => (role.featureScopes?.[id] ?? 0) >= 1
              ).length;
              const userCount = userCountByRole.get(role.id.toLowerCase()) ?? 0;
              return (
                <tr key={role.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                    <span className="inline-flex items-center gap-2">
                      {role.name}
                      {role.isSystem &&
                        (role.contactType === ContactType.Admin ||
                          role.contactType === ContactType.Driver ||
                          role.contactType === ContactType.Exporter) && (
                          <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                            Built-in
                          </span>
                        )}
                    </span>
                    {role.description && (
                      <p className="text-xs font-normal text-gray-500 mt-0.5">
                        {role.description}
                      </p>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {contactTypeLabel(role.contactType)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {userCount}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {enabledCount} feature{enabledCount === 1 ? "" : "s"}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium">
                    <button
                      type="button"
                      className="text-primary-600 hover:text-primary-900"
                      onClick={() => handleEdit(role)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="py-8 text-center text-sm text-gray-500"
                >
                  No roles found. Add a role to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
